import { hide } from './utils.js';

const initialisedBaskets = new WeakSet();
let basketDialogBound = false;
let basketControlsBound = false;

const getBasketDialogConfig = callback => ({
    modifiers: ['sidebar', 'basket'],
    loading: 'active',
    wait_for_scripts: true,
    callback,
});

const updateBasketCount = response => {
    if (!response) {
        return;
    }

    try {
        const data = typeof response === 'string' ? JSON.parse(response) : response;
        const count = data.payload?.basket_count ?? data.payload?.additional?.basketCount;
        if (count === undefined) {
            return;
        }
        document.querySelectorAll('[data-basket-count]').forEach(element => {
            element.textContent = String(count);
        });
    } catch (error) {
        console.error('Unable to read the updated basket count', error);
    }
};

const setBasketError = (basket, message = '') => {
    const error = basket?.querySelector('[data-basket-error]');
    if (!error) {
        return;
    }
    error.textContent = message;
    error.hidden = !message;
};

const setBasketCount = count => {
    document.querySelectorAll('[data-basket-count]').forEach(element => {
        element.textContent = String(count);
    });
};

const configureManagedDialog = dialog => {
    const target = dialog?.target;
    if (!target) {
        return;
    }

    const checkout = target.querySelector('[data-checkout-flow]');
    const basket = target.querySelector('[data-basket]');
    const close = target.querySelector('.dialog__close');

    if (checkout) {
        target.setAttribute('closedby', 'none');
        if (close) {
            close.hidden = true;
        }
        return;
    }

    if (basket) {
        setBasketCount(basket.dataset.basketItemCount ?? 0);
        target.setAttribute('closedby', 'any');
        if (close) {
            close.hidden = false;
        }
        initBasket(target);
    }
};

/** Open or refresh the session basket in the shared right-hand dialog. */
export const openBasketDialog = (url, root = document) => {
    if (!url || !window.Dialog) {
        return null;
    }

    return window.Dialog.load(
        url,
        getBasketDialogConfig(function callback(event) {
            updateBasketCount(event?.response);
            configureManagedDialog(this);
            initBasket(this.target || root);
            hide(document.querySelector('[data-loading-spinner]'));
        })
    );
};

/** Initialise controls within a full-page or dialog-loaded basket. */
export const initBasket = (root = document) => {
    if (!basketControlsBound) {
        basketControlsBound = true;
        document.addEventListener('click', event => {
            const basket = event.target.closest?.('[data-basket]');
            if (!basket) {
                return;
            }

            const personalisationToggle = event.target.closest?.(
                '[data-personalisation-toggle]'
            );
            if (personalisationToggle) {
                const panel = personalisationToggle
                    .closest('[data-personalisation]')
                    ?.querySelector('[data-personalisation-panel]');
                if (!panel) {
                    return;
                }
                const isExpanded =
                    personalisationToggle.getAttribute('aria-expanded') === 'true';
                personalisationToggle.setAttribute('aria-expanded', String(!isExpanded));
                panel.hidden = isExpanded;
                const label = personalisationToggle.querySelector(
                    '[data-personalisation-toggle-label]'
                );
                if (label) {
                    label.textContent = isExpanded
                        ? personalisationToggle.dataset.openLabel
                        : personalisationToggle.dataset.closeLabel;
                }
                return;
            }

            const quantityAdjust = event.target.closest?.('[data-quantity-adjust]');
            if (!quantityAdjust) {
                return;
            }

            event.preventDefault();
            const form = quantityAdjust.closest('[data-quantity-form]');
            const input = form?.querySelector('[data-quantity-input]');
            const output = form?.querySelector('[data-quantity-value]');
            if (!form || !input) {
                return;
            }
            const nextQuantity = Math.max(
                1,
                Number(input.value) + Number(quantityAdjust.dataset.quantityAdjust)
            );
            input.value = String(nextQuantity);
            if (output) {
                output.textContent = String(nextQuantity);
            }
            form.querySelector('[data-quantity-adjust="-1"]')?.toggleAttribute(
                'disabled',
                nextQuantity === 1
            );
            form.requestSubmit();
        });

        document.addEventListener('submit', async event => {
            const form = event.target.closest?.('[data-basket-mutation]');
            const basket = form?.closest('[data-basket]');
            if (!form || !basket) {
                return;
            }

            event.preventDefault();
            setBasketError(basket);
            form.closest('[data-bundle]')?.setAttribute('aria-busy', 'true');
            document.dispatchEvent(new CustomEvent('rea:loading', { detail: true }));

            try {
                const response = await fetch(form.action, {
                    method: form.method,
                    credentials: 'same-origin',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: new FormData(form),
                });
                const data = await response.json();
                if (!response.ok || !data.result) {
                    throw new Error(data.message || 'Unable to update your basket.');
                }

                const swaps = data.payload?.swaps ?? {};
                if (swaps['basket-count'] !== undefined) {
                    setBasketCount(swaps['basket-count']);
                }
                if (swaps.basket !== undefined) {
                    basket.outerHTML = swaps.basket;
                }
            } catch (error) {
                console.error('Unable to update basket', error);
                form.closest('[data-bundle]')?.removeAttribute('aria-busy');
                setBasketError(
                    basket,
                    'Sorry, we could not update your basket. Please try again.'
                );
            } finally {
                document.dispatchEvent(
                    new CustomEvent('rea:loading', { detail: false })
                );
            }
        });
    }

    root.querySelectorAll('[data-basket]').forEach(basket => {
        if (initialisedBaskets.has(basket)) {
            return;
        }
        initialisedBaskets.add(basket);

        basket.querySelectorAll('[data-engraving-message]').forEach(message => {
            const font = basket.dataset.motifFont;
            if (font) {
                message.style.fontFamily = `${message.style.fontFamily ? `${message.style.fontFamily}, ` : ''}${font}`;
            }
            message.querySelectorAll('svg[width][height]').forEach(svg => {
                svg.setAttribute(
                    'viewBox',
                    `0 0 ${svg.getAttribute('width')} ${svg.getAttribute('height')}`
                );
            });
        });
    });
};

const bindBasketDialogLifecycle = root => {
    if (basketDialogBound) {
        return;
    }
    basketDialogBound = true;

    // Handle Back before Dialog's delegated link handler reloads the basket into checkout.
    root.addEventListener('click', event => {
        const back = event.target.closest?.('[data-dialog-back][data-modifiers*="basket"]');
        if (!back) {
            return;
        }
        const manager = window.Dialog;
        const basketDialog = manager?.stack?.find(dialog =>
            dialog !== manager.current && dialog.target.querySelector('[data-basket]')
        );
        if (!basketDialog || !manager.current?.target.contains(back)) {
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        manager.close(() => configureManagedDialog(basketDialog));
    }, true);

    root.addEventListener('click', event => {
        const trigger = event.target.closest?.('[data-basket-dialog]');
        if (!trigger?.href || !window.Dialog) {
            return;
        }
        event.preventDefault();
        openBasketDialog(trigger.href, root);
    });

    root.addEventListener(
        'cancel',
        event => {
            if (event.target.querySelector?.('[data-checkout-flow]')) {
                event.preventDefault();
            }
        },
        true
    );

    window.Dialog?.Triggers?.addEventListener(
        'd3r.dialog.open',
        event => configureManagedDialog(event.detail?.overlay)
    );
};

/** Bind basket triggers and open the drawer when `dialog=basket` is present. */
export const initBasketDialog = (root = document) => {
    bindBasketDialogLifecycle(root);
    initBasket(root);

    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.get('dialog') !== 'basket') {
        return;
    }

    const basketUrl = root.querySelector('[data-basket-dialog]')?.href;
    currentUrl.searchParams.delete('dialog');
    window.history.replaceState(window.history.state, '', currentUrl);
    openBasketDialog(basketUrl, root);
};
